import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  AlertTriangle,
  Building,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Users,
  Eye,
  Pencil,
  Pause,
  PlayCircle,
  BarChart3
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SubscriptionDetailsModal from '../../components/admin/SubscriptionDetailsModal';
import EditSubscriptionModal from '../../components/admin/EditSubscriptionModal';
import Toast from '../../components/ui/Toast';
import { adminApi } from '../../services/api';
import { AdminSubscriptionRecord, PaginationInfo } from '../../types';
import { handleApiError } from '../../utils/errorHandling';
import { useSubscriptionError } from '../../context/SubscriptionErrorContext';

const ITEMS_PER_PAGE = 20;

interface SubscriptionStats {
  total: number;
  active: number;
  canceled: number;
  totalMRR: number;
}

const AllSubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const { setSubscriptionError } = useSubscriptionError();
  const [selectedSubscription, setSelectedSubscription] = useState<AdminSubscriptionRecord | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<AdminSubscriptionRecord | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllSubscriptions({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        status: statusFilter || undefined,
      });
      setSubscriptions(response.subscriptions || []);
      setPagination(response.pagination);
      
      // Calculate stats from first page (or could be from separate endpoint)
      if (currentPage === 1 && !statusFilter) {
        const subs = response.subscriptions || [];
        setStats({
          total: response.pagination.total,
          active: subs.filter(s => s.status === 'active').length,
          canceled: subs.filter(s => s.status === 'inactive' || s.status === 'suspended').length,
          totalMRR: subs
            .filter(s => s.status === 'active')
            .reduce((sum, s) => sum + (s.billing?.amount || 0), 0),
        });
      }
      
      setError(null);
    } catch (err) {
      handleApiError(err, setError, setSubscriptionError);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, setSubscriptionError]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Type badge configurations
  const getTypeBadge = (typeId: string) => {
    const typeConfigs: Record<string, string> = {
      standard: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      premium: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      trial: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      partner: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      reseller: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
      internal: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      vip: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    return typeConfigs[typeId] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  // Filter subscriptions by search term (client-side for current page)
  const filteredSubscriptions = subscriptions.filter(sub => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      sub.tenantId?.toLowerCase().includes(searchLower) ||
      sub.tenantName?.toLowerCase().includes(searchLower) ||
      sub.planId?.toLowerCase().includes(searchLower) ||
      sub.planName?.toLowerCase().includes(searchLower) ||
      sub.subscriptionId?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; icon: React.ReactNode }> = {
      active: { 
        bg: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle className="w-3.5 h-3.5" />
      },
      inactive: { 
        bg: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        icon: <XCircle className="w-3.5 h-3.5" />
      },
      suspended: { 
        bg: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        icon: <Pause className="w-3.5 h-3.5" />
      },
      pending: { 
        bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        icon: <Clock className="w-3.5 h-3.5" />
      },
      trialing: { 
        bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <PlayCircle className="w-3.5 h-3.5" />
      },
    };
    return config[status] || config.inactive;
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading && subscriptions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleViewDetails = (subscription: AdminSubscriptionRecord) => {
    setSelectedSubscription(subscription);
    setIsDetailsModalOpen(true);
  };

  const handleEditSubscription = (subscription: AdminSubscriptionRecord) => {
    setEditingSubscription(subscription);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = (updatedSubscription: AdminSubscriptionRecord) => {
    setIsEditModalOpen(false);
    setEditingSubscription(null);
    fetchSubscriptions();
    setNotification({ 
      type: 'success', 
      message: `Subscription for ${updatedSubscription.tenantName} updated successfully!` 
    });
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold dark:text-white text-gray-900 flex items-center gap-3">
          <CreditCard className="w-8 h-8" />
          All Subscriptions
        </h1>
        <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">
          Manage all tenant subscriptions and billing
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold dark:text-white text-gray-900">{stats.total}</p>
                <p className="text-sm dark:text-gray-400 text-gray-600">Total Subscriptions</p>
              </div>
            </div>
          </Card>
          
          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold dark:text-white text-gray-900">{stats.active}</p>
                <p className="text-sm dark:text-gray-400 text-gray-600">Active</p>
              </div>
            </div>
          </Card>
          
          <Card className="!p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold dark:text-white text-gray-900">
                  {formatCurrency(stats.totalMRR)}
                </p>
                <p className="text-sm dark:text-gray-400 text-gray-600">Est. MRR</p>
              </div>
            </div>
          </Card>

          <div 
            className="cursor-pointer group"
            onClick={() => navigate('/admin/subscriptions/insights')}
          >
            <Card className="!p-4 group-hover:border-primary/50 transition-all duration-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                  <BarChart3 className="w-5 h-5 text-violet-500" />
                </div>
                <div>
                  <p className="text-lg font-bold dark:text-white text-gray-900 group-hover:text-violet-500 transition-colors">Insights</p>
                  <p className="text-sm dark:text-gray-400 text-gray-600">View Analytics</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tenant, plan, or subscription ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="past_due">Past Due</option>
            <option value="canceled">Canceled</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Subscriptions Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-gray-700 border-gray-200">
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Tenant</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Type</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Plan</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Status</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Amount</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Next Billing</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Usage (cycle)</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Created</th>
                <th className="text-right py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="dark:text-gray-400 text-gray-600">
                      {searchTerm || statusFilter
                        ? 'No subscriptions match your filters'
                        : 'No subscriptions found'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.map((sub) => {
                  const statusConfig = getStatusBadge(sub.status);
                  return (
                    <tr 
                      key={sub.subscriptionId}
                      className="border-b dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium dark:text-white text-gray-900">
                              {sub.tenantName || sub.tenantId}
                            </p>
                            <p className="text-xs font-mono dark:text-gray-400 text-gray-600">
                              {sub.tenantId}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {sub.types && sub.types.length > 0 ? (
                            sub.types.map((type) => (
                              <span
                                key={type}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadge(type)}`}
                              >
                                {type}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm dark:text-gray-500 text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium dark:text-white text-gray-900 capitalize">
                          {sub.planName || sub.planId?.replace('-', ' ') || 'N/A'}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg}`}>
                          {statusConfig.icon}
                          {sub.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium dark:text-white text-gray-900">
                            {formatCurrency(sub.billing?.amount || 0)}
                          </span>
                          <span className="text-xs dark:text-gray-400 text-gray-600">/mo</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {sub.billing?.nextBillingDate ? (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 dark:text-gray-400 text-gray-500" />
                            <span className="text-sm dark:text-gray-300 text-gray-700">
                              {new Date(sub.billing.nextBillingDate).toLocaleDateString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm dark:text-gray-500 text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {sub.currentUsage ? (
                          <div className="text-sm">
                            <p className="dark:text-gray-300 text-gray-700">
                              {sub.currentUsage.apiCalls?.toLocaleString() || 0} calls
                            </p>
                            <p className="text-xs dark:text-gray-500 text-gray-500">
                              {sub.currentUsage.conversations?.toLocaleString() || 0} conversations
                            </p>
                          </div>
                        ) : (
                          <span className="text-sm dark:text-gray-500 text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 dark:text-gray-400 text-gray-600 text-sm">
                        {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            variant="secondary" 
                            size="icon"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEditSubscription(sub);
                            }}
                            className="hover:bg-primary/10 hover:text-primary transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="secondary" 
                            size="icon"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewDetails(sub);
                            }}
                            className="hover:bg-primary/10 hover:text-primary transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-700 border-gray-200">
            <p className="text-sm dark:text-gray-400 text-gray-600">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)} of {pagination.total} subscriptions
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm dark:text-gray-300 text-gray-700 px-3">
                Page {currentPage} of {pagination.pages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                disabled={currentPage === pagination.pages || loading}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
      
      <SubscriptionDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        subscription={selectedSubscription}
      />

      <EditSubscriptionModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingSubscription(null);
        }}
        subscription={editingSubscription}
        onSuccess={handleEditSuccess}
      />

      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default AllSubscriptionsPage;
